import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/state/SessionProvider";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useState } from "react";
import { CheckCircle2, XCircle, Clock, MapPin, Calendar } from "lucide-react";

export default function Admin() {
  const { user } = useSession();
  const navigate = useNavigate();
  const { data: roleData, isLoading: roleLoading } = useUserRole();
  const queryClient = useQueryClient();
  const [rejectionReasons, setRejectionReasons] = useState<Record<string, string>>({});

  // Redirect if not admin
  if (!roleLoading && !roleData?.isAdmin) {
    navigate("/");
    return null;
  }

  if (!user) {
    navigate("/auth?mode=signin&redirect=/admin");
    return null;
  }

  const { data: listings, isLoading } = useQuery({
    queryKey: ["admin-listings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select(`
          *,
          owner:users!listings_owner_id_fkey(id, name, email, phone)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (listingId: string) => {
      const { error } = await supabase
        .from("listings")
        .update({
          approval_status: "approved",
          approved_at: new Date().toISOString(),
          approved_by: user.id,
        })
        .eq("id", listingId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-listings"] });
      toast.success("Listing godkendt");
    },
    onError: () => {
      toast.error("Kunne ikke godkende listing");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ listingId, reason }: { listingId: string; reason: string }) => {
      const { error } = await supabase
        .from("listings")
        .update({
          approval_status: "rejected",
          rejection_reason: reason,
        })
        .eq("id", listingId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-listings"] });
      toast.success("Listing afvist");
      setRejectionReasons({});
    },
    onError: () => {
      toast.error("Kunne ikke afvise listing");
    },
  });

  const handleReject = (listingId: string) => {
    const reason = rejectionReasons[listingId];
    if (!reason?.trim()) {
      toast.error("Angiv venligst en årsag til afvisning");
      return;
    }
    rejectMutation.mutate({ listingId, reason });
  };

  if (roleLoading || isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container py-8">
          <div className="text-center">Indlæser...</div>
        </main>
        <Footer />
      </div>
    );
  }

  const pendingListings = listings?.filter((l) => l.approval_status === "pending") || [];
  const approvedListings = listings?.filter((l) => l.approval_status === "approved") || [];
  const rejectedListings = listings?.filter((l) => l.approval_status === "rejected") || [];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Admin Panel</h1>
          <p className="text-muted-foreground">Administrer og godkend parkeringspladser</p>
        </div>

        <div className="grid gap-4 md:grid-cols-3 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Afventer godkendelse</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pendingListings.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Godkendt</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{approvedListings.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Afvist</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{rejectedListings.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Listings */}
        {pendingListings.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Afventer godkendelse
            </h2>
            <div className="grid gap-4">
              {pendingListings.map((listing) => (
                <Card key={listing.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg">{listing.title}</CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <MapPin className="h-4 w-4" />
                          {listing.address}
                        </CardDescription>
                        <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          Oprettet {new Date(listing.created_at).toLocaleDateString("da-DK")}
                        </div>
                      </div>
                      <Badge variant="secondary">
                        <Clock className="h-3 w-3 mr-1" />
                        Afventer
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Photos */}
                      {listing.photos && listing.photos.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">Billeder ({listing.photos.length}):</p>
                          <div className="grid grid-cols-3 gap-2">
                            {listing.photos.map((photo, idx) => (
                              <a
                                key={idx}
                                href={photo}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="aspect-square rounded-lg overflow-hidden border hover:opacity-80 transition-opacity"
                              >
                                <img
                                  src={photo}
                                  alt={`Foto ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium mb-1">Beskrivelse:</p>
                        <p className="text-sm text-muted-foreground">{listing.description || "Ingen beskrivelse"}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="font-medium">Ejer:</p>
                          <p className="text-muted-foreground">{listing.owner?.name}</p>
                          <p className="text-muted-foreground">{listing.owner?.email}</p>
                        </div>
                        <div>
                          <p className="font-medium">Størrelse:</p>
                          <p className="text-muted-foreground">{listing.size}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => approveMutation.mutate(listing.id)}
                          disabled={approveMutation.isPending}
                          className="flex-1"
                        >
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Godkend
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => handleReject(listing.id)}
                          disabled={rejectMutation.isPending}
                          className="flex-1"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Afvis
                        </Button>
                      </div>
                      <Textarea
                        placeholder="Årsag til afvisning (påkrævet ved afvisning)"
                        value={rejectionReasons[listing.id] || ""}
                        onChange={(e) =>
                          setRejectionReasons((prev) => ({ ...prev, [listing.id]: e.target.value }))
                        }
                        className="min-h-[80px]"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Rejected Listings */}
        {rejectedListings.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-500" />
              Afviste pladser
            </h2>
            <div className="grid gap-4">
              {rejectedListings.map((listing) => (
                <Card key={listing.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg">{listing.title}</CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <MapPin className="h-4 w-4" />
                          {listing.address}
                        </CardDescription>
                        {listing.rejection_reason && (
                          <div className="mt-2 p-2 bg-red-50 dark:bg-red-950 rounded text-sm">
                            <p className="font-medium text-red-900 dark:text-red-100">Årsag til afvisning:</p>
                            <p className="text-red-800 dark:text-red-200">{listing.rejection_reason}</p>
                          </div>
                        )}
                      </div>
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" />
                        Afvist
                      </Badge>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        )}

        {listings?.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Ingen listings fundet
            </CardContent>
          </Card>
        )}
      </main>
      <Footer />
    </div>
  );
}
